import {useCallback, useEffect, useRef, useState} from 'react';

import {AddressSearchMode} from '../AddressFormV2/components/AddressSearchTypeSelector/types';
import {UseAddressFormV2Props} from './types';
import {useAddressForm} from './useAddressForm';
import {useAddressFormGeolocation} from './useAddressFormGeolocation';
import {applyReverseGeocodeToAddressForm} from './applyReverseGeocodeToAddressForm';

function useAddressFormV2({initialReverseGeocodeData, ...props}: UseAddressFormV2Props) {
	const {
		clearWarningInfo,
		fillAddressFields,
		handleChange,
		skipNextZipCodeFetch,
		resetFormFields,
		...addressForm
	} = useAddressForm(props);
	const [searchMode, setSearchMode] = useState<AddressSearchMode>(
		initialReverseGeocodeData ? AddressSearchMode.CURRENT_LOCATION : AddressSearchMode.ZIP_CODE,
	);

	const geolocation = useAddressFormGeolocation();
	const {requestLocation, resetGeolocation, reverseGeocodeData} = geolocation;
	const effectiveReverseGeocodeData = reverseGeocodeData ?? initialReverseGeocodeData ?? null;

	const appliedGeocodeDataRef = useRef<typeof effectiveReverseGeocodeData>(null);

	const handleSearchModeChange = useCallback(
		(mode: AddressSearchMode) => {
			resetFormFields();
			setSearchMode(mode);
			if (mode === AddressSearchMode.CURRENT_LOCATION) {
				appliedGeocodeDataRef.current = null;
				requestLocation();
				return;
			}

			resetGeolocation();
		},
		[requestLocation, resetFormFields, resetGeolocation],
	);

	useEffect(() => {
		if (props.address && searchMode === AddressSearchMode.ZIP_CODE) {
			fillAddressFields(props.address);
			skipNextZipCodeFetch();
			handleChange(props.address.nuCep);
			if (props.address.nuEndereco) {
				addressForm.methods.setValue('number', props.address.nuEndereco.toString());
			}

			return;
		}

		const shouldSkip =
			searchMode !== AddressSearchMode.CURRENT_LOCATION ||
			!effectiveReverseGeocodeData ||
			appliedGeocodeDataRef.current === effectiveReverseGeocodeData;

		if (shouldSkip) {
			return;
		}

		appliedGeocodeDataRef.current = effectiveReverseGeocodeData;

		applyReverseGeocodeToAddressForm({
			data: effectiveReverseGeocodeData,
			onZipCodeFilled: zipCode => {
				skipNextZipCodeFetch();
				handleChange(zipCode);
			},
			onAddressFieldsFilled: fillAddressFields,
			onWarningCleared: clearWarningInfo,
		});
	}, [
		clearWarningInfo,
		effectiveReverseGeocodeData,
		fillAddressFields,
		handleChange,
		searchMode,
		skipNextZipCodeFetch,
		props.address,
	]);

	const handleZipCodeFocus = useCallback(() => {
		if (searchMode === AddressSearchMode.CURRENT_LOCATION) {
			handleSearchModeChange(AddressSearchMode.ZIP_CODE);
		}
	}, [handleSearchModeChange, searchMode]);

	return {
		...addressForm,
		handleChange,
		searchMode,
		handleSearchModeChange,
		handleZipCodeFocus,
		geolocation,
	};
}

export {useAddressFormV2};
